import {
  Box,
  Typography,
  InputAdornment,
  IconButton,
  Tooltip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  forwardRef,
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { styled, keyframes } from "@mui/material/styles";
import { TextField } from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import MicNoneOutlinedIcon from "@mui/icons-material/MicNoneOutlined";
import MicOffIcon from "@mui/icons-material/MicOff";

const pulseGlow = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(25, 118, 210, 0.2);
  }
  50% {
    box-shadow: 0 0 0 4px rgba(25, 118, 210, 0.05);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(25, 118, 210, 0);
  }
`;

const submitPulse = keyframes`
  0%, 100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(148, 163, 184, 0.18);
  }
  50% {
    transform: scale(1.04);
    box-shadow: 0 0 0 6px rgba(148, 163, 184, 0.1);
  }
`;

const HINT_CYCLE_MS = 3200;
const HINT_EXIT_MS = 260;
const HINT_ENTER_MS = 420;

const hintEnter = keyframes`
  from {
    opacity: 0;
    transform: translateY(0.45em);
    filter: blur(2px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
    filter: blur(0);
  }
`;

const hintExit = keyframes`
  from {
    opacity: 1;
    transform: translateY(0);
    filter: blur(0);
  }
  to {
    opacity: 0;
    transform: translateY(-0.45em);
    filter: blur(2px);
  }
`;

const ModernSearchField = styled(TextField, {
  shouldForwardProp: (prop) => !["expanded"].includes(prop),
})(({ theme, expanded }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: "30px",
    backgroundColor:
      theme.palette.mode === "dark"
        ? "rgba(24, 30, 39, 0.92)"
        : theme.palette.background.paper,
    border: "none",
    boxShadow:
      theme.palette.mode === "dark"
        ? "0 8px 24px rgba(2, 6, 23, 0.42), inset 0 0 0 1px rgba(255,255,255,0.04)"
        : "0 4px 14px rgba(0, 0, 0, 0.13)",
    fontSize: "1.2rem",
    fontWeight: 500,
    width: "100%",
    height: "60px",
    cursor: "text",
    transition: "box-shadow 0.2s ease",
    "&:hover": {
      border: "none",
    },
    "&.Mui-focused": {
      border: "none",
      boxShadow:
        theme.palette.mode === "dark"
          ? "0 10px 26px rgba(2, 6, 23, 0.48), 0 0 0 3px rgba(250, 137, 107, 0.12)"
          : "0 4px 14px rgba(0, 0, 0, 0.13)",
    },
  },
  "& .MuiInputAdornment-root": {
    position: "relative",
    opacity: 1,
  },
  "& .MuiInputAdornment-positionStart": {
    paddingLeft: "0px",
  },
  "& .MuiOutlinedInput-input": {
    color: theme.palette.text.primary,
    padding: "16px 24px 16px 5px",
    fontSize: "17px",
    fontWeight: 400,
    opacity: 1,
    caretColor: theme.palette.primary.main,
    "&::placeholder": {
      color:
        theme.palette.mode === "dark"
          ? "rgba(240, 244, 247, 0.8)"
          : theme.palette.text.secondary,
      opacity: 1,
      fontWeight: 400,
      fontSize: "16px",
      transition: "none",
    },
    "&:focus::placeholder": {
      opacity: 0,
    },
  },
  "& .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
}));

// Create a styled wrapper for the icon
const StyledIconWrapper = styled(Box)({
  marginLeft: "8px",
  marginTop: "-2px",
  display: "flex",
  alignItems: "center",
});

const SearchInput = forwardRef(
  (
    {
      expanded,
      currentStep,
      searchData,
      fieldErrors,
      placeholder,
      onChange,
      onKeyDown,
      onFocus: onFocusExternal,
      onBlur: onBlurExternal,
      startAdornment,
      value,
      isMobile,
      onSubmit,
      submitDisabled = false,
      isLoading = false,
      isListening = false,
      speechSupported = false,
      onVoiceSearch,
    },
    ref,
  ) => {
    const theme = useTheme();
    const inputElementRef = useRef(null);
    const [hasUserInteracted, setHasUserInteracted] = useState(false);
    const [hint, setHint] = useState({ previous: null, current: 0 });
    const [isFocused, setIsFocused] = useState(false);
    const [isSubmitPending, setIsSubmitPending] = useState(false);
    const [submitSawLoading, setSubmitSawLoading] = useState(false);

    const placeholderExamples = useMemo(
      () => [
        "Sarah Chen at Stripe",
        "jane@company.com",
        "Product lead at Shopify",
        "Fractional CFO in fintech",
        "Founder in Toronto",
      ],
      [],
    );

    const getCurrentValue = useCallback(() => {
      if (value !== undefined) {
        return value;
      }

      switch (currentStep) {
        case 0:
          return searchData?.firstName || "";
        case 1:
          return searchData?.familyName || "";
        case 2:
          return searchData?.company || "";
        default:
          return "";
      }
    }, [value, currentStep, searchData]);

    const showRotatingHint =
      !isFocused && !isListening && !hasUserInteracted && !getCurrentValue();

    useEffect(() => {
      if (!showRotatingHint) return;
      const intervalId = setInterval(() => {
        setHint(({ current }) => ({
          previous: current,
          current: (current + 1) % placeholderExamples.length,
        }));
      }, HINT_CYCLE_MS);
      return () => {
        clearInterval(intervalId);
        setHint(({ current }) => ({ previous: null, current }));
      };
    }, [showRotatingHint, placeholderExamples.length]);

    // The forwarded ref points at the native input so callers can focus/read it.
    const setInputRef = useCallback(
      (node) => {
        inputElementRef.current = node;
        if (typeof ref === "function") ref(node);
        else if (ref) ref.current = node;
      },
      [ref],
    );

    // Any click inside the rounded field (padding, hint text, icons) focuses the input.
    const handleShellMouseDown = (event) => {
      const input = inputElementRef.current;
      if (!input) return;
      if (event.target === input) return;
      if (event.target.closest?.("button, a, [role='button'], input")) return;
      event.preventDefault();
      input.focus();
    };

    const handleUserInteraction = () => {
      setHasUserInteracted(true);
    };

    const handleChange = (e) => {
      handleUserInteraction();
      if (onChange) onChange(e);
    };

    const handleFocus = () => {
      setIsFocused(true);
      handleUserInteraction();
      if (onFocusExternal) onFocusExternal();
    };

    const handleBlur = () => {
      setIsFocused(false);
      if (!getCurrentValue()) {
        setHasUserInteracted(false);
      }
      if (onBlurExternal) onBlurExternal();
    };

    const getCurrentError = () => {
      switch (currentStep) {
        case 0:
          return fieldErrors?.firstName;
        case 1:
          return fieldErrors?.familyName;
        case 2:
          return fieldErrors?.company;
        default:
          return "";
      }
    };

    const hasError = expanded && !!getCurrentError();

    const nativeInputProps = {
      autoComplete: "off",
      autoCorrect: "off",
      autoCapitalize: "words",
      spellCheck: false,
      inputMode: "text",
      enterKeyHint: "search",
    };

    const currentPlaceholder = isFocused
      ? ""
      : isListening
      ? "Listening..."
      : hasUserInteracted
      ? placeholder
      : "";

    useEffect(() => {
      if (isSubmitPending && isLoading) {
        setSubmitSawLoading(true);
      }

      if (isSubmitPending && submitSawLoading && !isLoading) {
        setIsSubmitPending(false);
        setSubmitSawLoading(false);
      }
    }, [isLoading, isSubmitPending, submitSawLoading]);

    const showSubmitLoadingState = isLoading || isSubmitPending;

    const triggerSubmitPending = () => {
      setIsSubmitPending(true);
      setSubmitSawLoading(false);
    };

    const handleSubmitClick = () => {
      if (submitDisabled || isLoading || !onSubmit) return;
      triggerSubmitPending();
      onSubmit();
    };

    const handleInputKeyDown = (event) => {
      if (event.key === "Enter" && !submitDisabled && !isLoading) {
        triggerSubmitPending();
      }

      if (onKeyDown) onKeyDown(event);
    };

    // A native placeholder can't animate, so the hint lives in the start adornment.
    const rotatingHint = showRotatingHint ? (
      <Box
        component="span"
        aria-hidden="true"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          pl: "5px",
          whiteSpace: "nowrap",
          pointerEvents: "none",
          fontSize: "16px",
          fontWeight: 400,
          color:
            theme.palette.mode === "dark"
              ? "rgba(240, 244, 247, 0.8)"
              : theme.palette.text.secondary,
        }}
      >
        Try&nbsp;
        {/* Outgoing and incoming examples share one grid cell so width stays stable. */}
        <Box
          component="span"
          sx={{
            display: "inline-grid",
            justifyItems: "start",
            lineHeight: "1.6em",
            "& > span": {
              gridArea: "1 / 1",
              "@media (prefers-reduced-motion: reduce)": {
                animation: "none !important",
              },
            },
          }}
        >
          {hint.previous !== null && (
            <Box
              key={`out-${hint.previous}-${hint.current}`}
              component="span"
              sx={{
                animation: `${hintExit} ${HINT_EXIT_MS}ms ease-in both`,
                "@media (prefers-reduced-motion: reduce)": {
                  visibility: "hidden",
                },
              }}
            >
              “{placeholderExamples[hint.previous]}”
            </Box>
          )}
          <Box
            key={`in-${hint.current}`}
            component="span"
            sx={{
              // Waits for the outgoing example to finish so the two never overlap.
              animation: `${hintEnter} ${HINT_ENTER_MS}ms cubic-bezier(0.22, 1, 0.36, 1) ${
                hint.previous !== null ? HINT_EXIT_MS : 0
              }ms both`,
            }}
          >
            “{placeholderExamples[hint.current]}”
          </Box>
        </Box>
      </Box>
    ) : null;

    // Wrap the startAdornment in our styled wrapper
    const wrappedStartAdornment =
      startAdornment || rotatingHint ? (
        <InputAdornment position="start" sx={{ flexShrink: 0 }}>
          {startAdornment && (
            <StyledIconWrapper>
              {startAdornment.props?.children || startAdornment}
            </StyledIconWrapper>
          )}
          {rotatingHint}
        </InputAdornment>
      ) : null;

    const hasSubmitAction = !!onSubmit;
    const isSubmitDisabled = submitDisabled || showSubmitLoadingState;

    const wrappedEndAdornment = hasSubmitAction ? (
      <InputAdornment position="end" sx={{ mr: 1.75 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
          {speechSupported && (
            <Tooltip
              title={isListening ? "Stop listening" : "Voice search"}
              arrow
              placement="top"
            >
              <IconButton
                type="button"
                onClick={onVoiceSearch}
                aria-label={isListening ? "Stop listening" : "Voice search"}
                sx={{
                  p: 1,
                  mr: 1,
                  color: isListening ? "#fff" : "inherit",
                  backgroundColor: isListening ? "error.main" : "transparent",
                  boxShadow: isListening
                    ? "0 0 20px rgba(244, 67, 54, 0.6)"
                    : "none",
                  "&:hover": {
                    backgroundColor: isListening
                      ? "error.main"
                      : "primary.light",
                    transform: isListening ? "scale(1.05)" : "scale(1)",
                  },
                  animation: isListening ? "recording 2s infinite" : "none",
                  transition: "all 0.3s ease",
                }}
              >
                {isListening ? <MicOffIcon /> : <MicNoneOutlinedIcon />}
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Search" arrow placement="top">
            <span>
              <IconButton
                onClick={isSubmitDisabled ? undefined : handleSubmitClick}
                edge="end"
                aria-label="submit search"
                disabled={isSubmitDisabled}
                sx={{
                  width: "32px",
                  height: "32px",
                  color: theme.palette.primary.contrastText,
                  backgroundColor: theme.palette.primary.main,
                  borderRadius: "50%",
                  animation: showSubmitLoadingState
                    ? `${submitPulse} 1.2s ease-in-out infinite`
                    : "none",
                  "& .MuiSvgIcon-root": {
                    color: "inherit",
                  },
                  "&:hover": {
                    backgroundColor: theme.palette.primary.dark,
                    color: theme.palette.primary.contrastText,
                  },
                  "&:hover .MuiSvgIcon-root": {
                    color: theme.palette.primary.contrastText,
                  },
                  "&:active": {
                    backgroundColor: theme.palette.primary.dark,
                    color: theme.palette.primary.contrastText,
                  },
                  "&:active .MuiSvgIcon-root": {
                    color: theme.palette.primary.contrastText,
                  },
                  "&.Mui-disabled": {
                    color: theme.palette.action.disabled,
                    backgroundColor: theme.palette.action.disabledBackground,
                    opacity: 1,
                  },
                }}
              >
                <ArrowUpwardIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </InputAdornment>
    ) : null;

    return (
      <Box
        sx={{ flex: 1, position: "relative" }}
        onMouseDown={handleShellMouseDown}
      >
        <ModernSearchField
          inputRef={setInputRef}
          expanded={expanded}
          fullWidth
          placeholder={currentPlaceholder}
          variant="outlined"
          value={getCurrentValue()}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleInputKeyDown}
          error={hasError}
          slotProps={{
            input: {
              ...nativeInputProps,
              startAdornment: wrappedStartAdornment,
              endAdornment: wrappedEndAdornment,
            },
          }}
          autoFocus={true}
          sx={{
            "& .MuiOutlinedInput-root": {
              ...(hasError && {
                border: "none",
                "&:hover": {
                  border: "none",
                },
                "&.Mui-focused": {
                  border: "none",
                  boxShadow: "none",
                },
              }),
            },
          }}
        />

        {hasError && (
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.error.main,
              position: "absolute",
              top: -20,
              left: 30,
              fontSize: "0.75rem",
              backgroundColor: theme.palette.background.default,
              padding: "0 4px",
              borderRadius: "4px",
            }}
          >
            {getCurrentError()}
          </Typography>
        )}
      </Box>
    );
  },
);

SearchInput.displayName = "SearchInput";

export default SearchInput;
